import React, {
  useContext,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from "react";
import { CircularProgress, Typography } from "@mui/material";

import { GlobalContext } from "@/helpers/contexts/contexts";
import DigitalCampaignListBase from "../components/DigitalCampaignList";
import PageLayoutBase from "@/components/layouts/page/PageLayout";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import useInfiniteFetcher from "@/helpers/hooks/useInfiniteFetcher";
import useAutoLoadUntilScrollable from "@/helpers/hooks/useAutoLoadUntilScrollable";
import useFetcher from "@/helpers/hooks/useFetcher";
import CampaignListFiltersBase from "@/features/campaigns/shared/components/CampaignListFilters";

const PAGE_SIZE = 12;

type FilterItem = {
  id: string | number;
  [key: string]: unknown;
};

type DigitalCampaignFilters = {
  tags: FilterItem[];
  channels: FilterItem[];
  search: string;
};

type DigitalCampaignContext = {
  loggedInUser?: {
    participantData?: {
      id?: string | number;
      system_generated_participant_id?: string;
    };
  } | null;
  selectedBrand?: {
    id?: string | number;
  } | null;
};

type DigitalCampaignPage = {
  campaignTabsData?: {
    data?: Array<Record<string, unknown>>;
    pageCount?: number;
  };
};

type TagsAndChannelsResponse = {
  tagsList?: FilterItem[];
  channels?: FilterItem[];
};

const PageLayout = PageLayoutBase as React.ComponentType<any>;
const CampaignListFilters = CampaignListFiltersBase as React.ComponentType<any>;
const DigitalCampaignList = DigitalCampaignListBase as React.ComponentType<any>;

const DigitalCampaignListPage = () => {
  const { loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as DigitalCampaignContext;
  const [filters, setFilters] = useState<DigitalCampaignFilters>({
    tags: [],
    channels: [],
    search: "",
  });

  const normalizedSearch = filters.search.trim();
  const selectedTagIds = useMemo(
    () => filters.tags.map((tag) => tag.id).join(","),
    [filters.tags]
  );
  const selectedChannelIds = useMemo(
    () => filters.channels.map((channel) => channel.id).join(","),
    [filters.channels]
  );
  const hasAppliedFilters = Boolean(
    normalizedSearch || selectedTagIds || selectedChannelIds
  );

  const reqData = useMemo(() => {
    if (!selectedBrand?.id || !loggedInUser?.participantData?.id) return null;
    return {
      id: selectedBrand?.id,
      fk_participant_id: loggedInUser?.participantData?.id,
      participant_id:
        loggedInUser?.participantData?.system_generated_participant_id,
      userId: loggedInUser?.participantData?.id,
      offset: 0,
      limit: PAGE_SIZE,
      ...(hasAppliedFilters && {
        ...(normalizedSearch && { campaign_name: normalizedSearch }),
        ...(selectedTagIds && { tag_id: selectedTagIds }),
        ...(selectedChannelIds && { channel_id: selectedChannelIds }),
      }),
    };
  }, [
    selectedBrand?.id,
    loggedInUser,
    hasAppliedFilters,
    normalizedSearch,
    selectedChannelIds,
    selectedTagIds,
  ]);

  const { data, isLoading, isValidating, size, setSize } =
    useInfiniteFetcher<DigitalCampaignPage>(
      reqData,
      campaignService.getDigitalCampaigns,
      { revalidateOnFocus: false, parallel: true }
    );

  const { data: tagsAndChannelsData } = useFetcher<TagsAndChannelsResponse>(
    loggedInUser?.participantData?.id && selectedBrand?.id
      ? {
          fk_participant_id: loggedInUser.participantData.id,
          id: selectedBrand.id,
        }
      : null,
    campaignService.getTagsAndChannelsForParticipant,
    { revalidateOnFocus: false }
  );

  const handleFilterChange = useCallback(
    (filterType: string, value: unknown) => {
      setFilters((prev) => ({ ...prev, [filterType]: value }));
    },
    []
  );

  const handleSearchChange = useCallback(
    (value: string) => handleFilterChange("search", value),
    [handleFilterChange]
  );

  const campaigns = data?.flatMap((d) => d?.campaignTabsData?.data || []) ?? [];
  const lastPage = data?.[data.length - 1]?.campaignTabsData?.pageCount;
  const isLastPage = size >= lastPage;
  const hasMore = !isLastPage;

  useAutoLoadUntilScrollable({
    containerId: "launch-camp",
    isLastPage,
    dependency: campaigns.length,
    triggerLoad: () => {
      if (!isValidating && data?.length > 0) setSize((prev) => prev + 1);
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrolledTo = window.scrollY + window.innerHeight;
      const isNearBottom = document.body.scrollHeight - 400 <= scrolledTo;
      if (isNearBottom && !isLastPage && !isValidating) {
        setSize((prev) => prev + 1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLastPage, isValidating, setSize]);

  return (
    <PageLayout className="participant-dashboard-container mx-0 mx-sm-2">
      <div className="dashboard participant-dashboard pb-5" id="launch-camp">
        <div className="row justify-content-end align-items-center my-3">
          <div className="col">
            <Typography variant="h5" className="widget-title mb-4 mb-md-auto">
              Launch Campaign
            </Typography>
          </div>
        </div>
        <CampaignListFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          searchValue={filters.search}
          onSearchChange={handleSearchChange}
          tagsList={tagsAndChannelsData?.tagsList || []}
          channelsList={tagsAndChannelsData?.channels || []}
          isLoading={isLoading}
        />
        <DigitalCampaignList
          campaigns={campaigns}
          isThereAnyActiveSubscription
          isLoading={isLoading}
          searchValue={filters.search}
        />
        {!isLoading && isValidating && hasMore && (
          <div className="text-center bg-light">
            <Typography variant="caption" color="text.secondary">
              <CircularProgress color="primary" size={10} className="mr-1" />{" "}
              Loading ...
            </Typography>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default DigitalCampaignListPage;
